import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Languages, Search, Plus, Pencil, Sparkles, RefreshCw, Mail, Loader2, Building2 } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { Drawer } from '../../components/ui/Drawer';
import {
  useVendors,
  useRefreshJpInvoice,
  useVendorSemanticSearch,
  useVendorInvitations,
  useInviteVendor,
  Vendor,
} from '../../hooks/useVendors';
import { formatAmount, formatDate } from '../../lib/format';
import { useShortcut } from '../../components/KeyboardShortcutsProvider';
import { VatIdValidator } from '../../components/VatIdValidator';
import { VendorTaxForms } from '../../components/VendorTaxForms';
import { VendorFormDrawer } from '../../components/VendorFormDrawer';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import { useCan } from '../../hooks/useCan';

const FURIGANA_KEY = 'plyez.showFurigana';
const LANGS = ['en', 'ja', 'de', 'fr', 'es', 'pt', 'zh', 'ko'];

export function VendorsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [smartSearch, setSmartSearch] = useState(false);
  const [showFurigana, setShowFurigana] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(FURIGANA_KEY) === '1';
  });
  const { data: vendors, isLoading, isError, refetch } = useVendors(wsId, q ? { namePrefix: q } : {});

  // Smart (semantic) search: opt-in toggle, OR auto when the plain prefix
  // returns too few hits. Plain prefix stays the default.
  const fewPlainHits = !!q && !isLoading && (vendors?.length ?? 0) <= 1;
  const semanticEnabled = !!q && (smartSearch || fewPlainHits);
  const semantic = useVendorSemanticSearch(wsId, q, semanticEnabled);

  // Merge semantic hits not already in the prefix list (de-dupe by id).
  const displayVendors = useMemo<Vendor[]>(() => {
    const base = vendors ?? [];
    if (!semanticEnabled || !semantic.data?.length) return base;
    const seen = new Set(base.map((v) => v.id));
    const extra = semantic.data.filter((v) => !seen.has(v.id));
    return [...base, ...extra];
  }, [vendors, semantic.data, semanticEnabled]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const refreshJp = useRefreshJpInvoice(wsId);
  const selected = useMemo<Vendor | null>(
    () => displayVendors.find((v) => v.id === selectedId) ?? null,
    [displayVendors, selectedId],
  );

  const onRefreshJp = async () => {
    if (!selected) return;
    try {
      await refreshJp.mutateAsync(selected.id);
      toast.success(t('vendor.refreshJpDone'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('vendor.refreshJpFailed'));
    }
  };

  // Keyboard nav inside the list (spec §3.D).
  useShortcut('j', () => {
    if (!displayVendors.length) return;
    const i = selectedId ? displayVendors.findIndex((v) => v.id === selectedId) : -1;
    setSelectedId(displayVendors[Math.min(i + 1, displayVendors.length - 1)].id);
  }, t('shortcuts.nextRow'));
  useShortcut('k', () => {
    if (!displayVendors.length) return;
    const i = selectedId ? displayVendors.findIndex((v) => v.id === selectedId) : 0;
    setSelectedId(displayVendors[Math.max(i - 1, 0)].id);
  }, t('shortcuts.prevRow'));
  useShortcut('Escape', () => setSelectedId(null), t('shortcuts.closeDrawer'));

  function toggleFurigana() {
    const next = !showFurigana;
    setShowFurigana(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(FURIGANA_KEY, next ? '1' : '0');
  }

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="display text-2xl font-semibold">{t('vendors.title')}</h1>
          <div className="flex items-center gap-2">
            <Button
              variant={showFurigana ? 'primary' : 'ghost'}
              size="sm"
              onClick={toggleFurigana}
              aria-pressed={showFurigana}
              title={t('vendors.furiganaHint')}
            >
              <Languages size={14} /> {t('vendors.furigana')}
            </Button>
            <div className="flex items-center gap-2 h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900">
              <Search size={14} className="text-ink-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('common.searchPlaceholder')}
                className="bg-transparent border-0 outline-none text-sm w-48"
              />
            </div>
            <Button
              variant={smartSearch ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setSmartSearch((s) => !s)}
              aria-pressed={smartSearch}
              title={t('vendor.smartSearchHint')}
            >
              <Sparkles size={14} /> {t('vendor.smartSearch')}
            </Button>
            {canMutate && (
              <>
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium"
                >
                  <Mail size={14} /> {t('vendor.invite')}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditVendor(null); setFormOpen(true); }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
                >
                  <Plus size={14} /> {t('vendors.newVendor')}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mb-6">
          <AiInsightCard
            agent="vendor_risk"
            title={t('aiOutput.cards.vendorRisk')}
            prompt={t('aiOutput.prompts.vendorRisk')}
          />
        </div>

        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : isError ? (
            <QueryError onRetry={refetch} />
          ) : displayVendors.length === 0 ? (
            <EmptyState
              icon={Building2}
              title={t('emptyState.vendors.title')}
              description={t('emptyState.vendors.body')}
              primaryLabel={t('vendors.newVendor')}
              primaryIcon={Plus}
              onPrimary={() => { setEditVendor(null); setFormOpen(true); }}
              offerSampleData={!q}
              wsId={wsId}
            />
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {semanticEnabled && semantic.data && semantic.data.length > 0 && (
                <li className="px-5 py-2 text-[11px] uppercase tracking-wide text-ink-400 flex items-center gap-1.5">
                  <Sparkles size={11} /> {t('vendor.smartResults')}
                </li>
              )}
              {displayVendors.map((v) => (
                <li
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  className={`px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900 ${selectedId === v.id ? 'bg-accent-50/40 dark:bg-accent-900/20' : ''}`}
                >
                  <div className="min-w-0">
                    {showFurigana && v.legal_name_kana ? (
                      <div className="text-[11px] text-ink-500 mb-0.5">{v.legal_name_kana}</div>
                    ) : null}
                    <div className="font-medium">{v.legal_name}</div>
                    <div className="text-xs text-ink-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      {v.country_code && <Pill tone="muted">{v.country_code}</Pill>}
                      {v.jp_corp_number && <span className="font-mono text-[10px]">{v.jp_corp_number}</span>}
                      {v.jp_invoice_registration && (
                        <Pill tone={v.jp_invoice_status === 'active' ? 'success' : 'warn'}>
                          {(v.jp_invoice_status === 'active' ? t('vendors.qualifiedActive') : t('vendors.qualifiedRevoked'))} {v.jp_invoice_registration}
                        </Pill>
                      )}
                      {v.preferred_rail && <span className="text-[10px] uppercase">{v.preferred_rail}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="num text-sm font-medium">{formatAmount(v.total_spend_cents, v.default_currency ?? 'JPY', i18n.language)}</div>
                    <div className="text-xs text-ink-500">{t('vendors.totalSpend')}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Drawer
        open={!!selected}
        onOpenChange={(o) => !o && setSelectedId(null)}
        title={selected?.legal_name ?? ''}
        description={selected?.legal_name_kana ?? undefined}
        footer={
          selected && canMutate ? (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onRefreshJp}
                disabled={refreshJp.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
              >
                {refreshJp.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {t('vendor.refreshJp')}
              </button>
              <button
                type="button"
                onClick={() => { setEditVendor(selected); setFormOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
              >
                <Pencil size={12} /> {t('common.edit')}
              </button>
            </div>
          ) : null
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <Section title={t('vendors.drawer.identity')}>
              <Field label={t('vendors.drawer.country')} value={selected.country_code ?? '—'} />
              <Field label={t('vendors.drawer.jpCorp')} value={selected.jp_corp_number ?? '—'} mono />
              <Field label={t('vendors.drawer.jpInvoice')} value={selected.jp_invoice_registration ?? '—'} mono />
              <Field label={t('vendors.drawer.status')} value={selected.status} />
              <Field label={t('vendors.drawer.kyc')} value={selected.kyc_status} />
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-ink-400">
                  {t('vendors.drawer.taxId')}
                </div>
                <div className="mt-0.5">
                  {wsId && <VatIdValidator wsId={wsId} taxId={selected.tax_id} />}
                </div>
              </div>
              <Field label={t('vendors.drawer.taxFormKind')} value={selected.tax_form_kind ?? '—'} />
              {wsId && <VendorTaxForms wsId={wsId} vendorId={selected.id} />}
            </Section>
            <Section title={t('vendors.drawer.banking')}>
              {selected.banking ? (
                <>
                  <Field label={t('vendors.drawer.bankingKind')} value={selected.banking.kind} />
                  {Object.entries(selected.banking.fields).map(([k, v]) => (
                    <Field key={k} label={k} value={String(v)} mono />
                  ))}
                </>
              ) : (
                <div className="text-ink-500">{t('vendors.drawer.noBanking')}</div>
              )}
              <Field label={t('vendors.drawer.preferredRail')} value={selected.preferred_rail ?? '—'} />
              <Field label={t('vendors.drawer.defaultCurrency')} value={selected.default_currency ?? '—'} mono />
            </Section>
            <Section title={t('vendors.drawer.contact')}>
              <Field label={t('common.email')} value={selected.email ?? '—'} />
              <Field label={t('vendors.drawer.phone')} value={selected.phone ?? '—'} />
              <Field label={t('vendors.drawer.address')} value={selected.address ?? '—'} />
              <Field label={t('vendors.drawer.created')} value={formatDate(selected.created_at, i18n.language)} />
            </Section>
          </div>
        )}
      </Drawer>

      {wsId && (
        <VendorFormDrawer
          wsId={wsId}
          open={formOpen}
          onOpenChange={setFormOpen}
          vendor={editVendor}
          onSaved={(id) => setSelectedId(id)}
        />
      )}

      {wsId && canMutate && (
        <InviteVendorDrawer wsId={wsId} open={inviteOpen} onOpenChange={setInviteOpen} />
      )}
    </AppShell>
  );
}

function InviteVendorDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const invitations = useVendorInvitations(wsId);
  const invite = useInviteVendor(wsId);
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState(i18n.language?.split('-')[0] || 'en');
  const [error, setError] = useState<string | null>(null);

  const onInvite = async () => {
    setError(null);
    if (!email.trim() || !email.includes('@')) {
      setError(t('vendor.inviteEmailRequired'));
      return;
    }
    try {
      await invite.mutateAsync({ email: email.trim(), language });
      setEmail('');
      toast.success(t('vendor.inviteSent'));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? t('vendor.inviteFailed'));
    }
  };

  const pending = invitations.data ?? [];

  return (
    <Drawer open={open} onOpenChange={onOpenChange} size="md" title={t('vendor.inviteTitle')}>
      <div className="space-y-5 text-sm">
        <p className="text-xs text-ink-500 leading-relaxed">{t('vendor.inviteDescription')}</p>
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-ink-500">{t('common.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vendor@example.com"
              className="mt-0.5 w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-500">{t('vendor.inviteLanguage')}</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-0.5 w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {LANGS.map((l) => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={onInvite}
          disabled={invite.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 disabled:opacity-50"
        >
          {invite.isPending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {t('vendor.inviteSend')}
        </button>

        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-ink-500 mb-2">{t('vendor.invitePending')}</div>
          {invitations.isLoading ? (
            <div className="text-ink-500 text-xs py-4">{t('common.loading')}</div>
          ) : pending.length === 0 ? (
            <div className="text-ink-500 text-xs py-4">{t('vendor.inviteNone')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800 rounded-lg border border-ink-200 dark:border-ink-800">
              {pending.map((inv) => (
                <li key={inv.id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate">{inv.email}</div>
                    <div className="text-[11px] text-ink-500">{formatDate(inv.invited_at, i18n.language)}</div>
                  </div>
                  <Pill tone={inv.submitted_at ? 'success' : 'accent'}>
                    {inv.submitted_at ? t('vendor.inviteStatusSubmitted') : t('vendor.inviteStatusPending')}
                  </Pill>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-ink-500 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-ink-400">{label}</div>
      <div className={mono ? 'num text-sm mt-0.5 break-all' : 'text-sm mt-0.5'}>{value}</div>
    </div>
  );
}
